import React from 'react';
import { Upload, Spin, Button, Typography } from 'antd';
import { PlusCircleFilled } from '@ant-design/icons';
import { ILsRagDocument, ILsRagSession } from '../../../libs/services/LifeSciencesRagService/LifeSciencesRagService';

// isRag: drives RAG-specific chat UX (hints, "Induce Hallucination", the
// pgvector subtitle) — deliberately separate from supportsManualOverride,
// since MA/LZ/DC support model selection too but have none of the RAG UX.
// allowedModels mirrors each agent's backend AgentConfig.allowed_tiers
// (features/agents/registry.py), translated to the corresponding
// AVAILABLE_MODELS ids, so ModelBar doesn't offer a choice the router would
// just clamp away. undefined = every model in AVAILABLE_MODELS — ls-rag used
// to rely on this, but now lists its models explicitly instead so adding a
// model to the global list (e.g. olmo-3-7b) doesn't silently expose it here
// too; it's scoped to ls-rag on purpose (future fine-tuning target).
//
// obs mirrors the backend's ObservabilityCapabilities (features/agents/registry.py)
// — which of the 8 Agent Observability tabs apply to this agent. Only
// retrieval_quality/drift_data_quality are RAG-only; everything else is
// agent-agnostic in concept, so it defaults true for every active agent.
export interface IAgentObsCapabilities {
  retrievalQuality: boolean;
  driftDataQuality: boolean;
  generationQuality: boolean;
  hallucinationSafety: boolean;
  reliability: boolean;
  costEfficiency: boolean;
  userExperience: boolean;
  humanInTheLoop: boolean;
  // Agent Orchestration & Workflow tab — only meaningful for a multi-step
  // agent (one that fans out to sub-agents); single-call agents always read
  // "1 step, no failures," which is real but uninteresting, so it's off by
  // default and only flipped on per-agent below (Migration Assistant today).
  orchestration: boolean;
  // Tool & MCP Integration Health tab — only meaningful for an agent with
  // MCP/tool servers configured (backend AgentConfig.mcp_servers); off by
  // default, flipped on per-agent below (DevOps Copilot's GitHub tools today).
  toolMcpHealth: boolean;
}

const RAG_OBS: IAgentObsCapabilities = {
  retrievalQuality: true, driftDataQuality: true, generationQuality: true, hallucinationSafety: true,
  reliability: true, costEfficiency: true, userExperience: true, humanInTheLoop: true, orchestration: false,
  toolMcpHealth: false,
};
// Generation Quality / Hallucination & Safety / Human-in-the-Loop stay off
// for these 3 agents, same as Retrieval Quality and Drift & Data Quality —
// NOT because nothing is measured (agent_chat() in agents_routes.py already
// fires a background accuracy judge, accuracy_judge_service.py, writing
// real coherence/completeness/toxicity/PII/injection scores into the same
// columns get_metrics() aggregates), but because only a thin subset of the
// Agent Accuracy tab's cards apply to a non-RAG agent — RAGAS/groundedness/
// citation accuracy never populate, since there's no retrieval step. A tab
// with half its cards permanently blank read as broken rather than honest,
// so it's hidden (disabled, with an info tooltip explaining why) rather
// than shown half-empty. The data is still being collected underneath —
// flip these back on once there's enough of it to fill the tab out
// properly (e.g. once Migration Assistant's sub-agent-faithfulness check or
// a fuller reasoning-agent rubric lands).
const AGENT_OBS: IAgentObsCapabilities = {
  retrievalQuality: false, driftDataQuality: false, generationQuality: false, hallucinationSafety: false,
  reliability: true, costEfficiency: true, userExperience: true, humanInTheLoop: false, orchestration: false,
  toolMcpHealth: false,
};
// Exported for the Observability panel's "All Agents" pill — none of the 8
// per-metric tabs make sense aggregated across heterogeneous agents, so
// selecting "All" reuses this same all-false capability set to show the
// same "pick a specific agent" empty state every tab already has.
export const NONE_OBS: IAgentObsCapabilities = {
  retrievalQuality: false, driftDataQuality: false, generationQuality: false, hallucinationSafety: false,
  reliability: false, costEfficiency: false, userExperience: false, humanInTheLoop: false, orchestration: false,
  toolMcpHealth: false,
};

// Active agents first, "coming soon" ones grouped at the bottom — the
// sidebar renders in array order, so this ordering is what the user sees.
export const AGENTS = [
  { initials: 'SL', cls: 'av-gr', name: 'Scientific Lit. FAQ',   model: 'gemini-3.1-pro-preview', status: 'ok', active: true,  agentId: null,                   isRag: true,  supportsManualOverride: true,  supportsCostEstimate: true,  allowedModels: ['gemini/gemini-3.1-pro-preview', 'claude-sonnet-5', 'claude-haiku-4-5', 'gemma-4', 'olmo-3-7b'] as string[] | undefined, obs: RAG_OBS },
  { initials: 'MA', cls: 'av-bl', name: 'Migration Assistant',    model: 'Claude Sonnet 5',        status: 'ok', active: true,  agentId: 'migration-assistant',  isRag: false, supportsManualOverride: true,  supportsCostEstimate: true,  allowedModels: ['claude-sonnet-5', 'gemini/gemini-3.1-pro-preview'] as string[] | undefined, obs: { ...AGENT_OBS, orchestration: true } },
  { initials: 'LZ', cls: 'av-te', name: 'Landing Zone Advisor',   model: 'Claude Haiku 4.5',       status: 'ok', active: true,  agentId: 'landing-zone-advisor', isRag: false, supportsManualOverride: true,  supportsCostEstimate: true,  allowedModels: ['claude-haiku-4-5', 'claude-sonnet-5', 'gemini/gemini-3.1-pro-preview'] as string[] | undefined, obs: AGENT_OBS },
  { initials: 'DC', cls: 'av-pu', name: 'DevOps Copilot',         model: 'Claude Haiku 4.5',       status: 'ok', active: true,  agentId: 'devops-copilot',       isRag: false, supportsManualOverride: true,  supportsCostEstimate: true,  allowedModels: ['gemma-4', 'claude-haiku-4-5', 'claude-sonnet-5'] as string[] | undefined, obs: { ...AGENT_OBS, toolMcpHealth: true } },
  { initials: 'O',  cls: 'av-or', name: 'Orchestrator',           model: 'Claude Opus',            status: 'ok', active: false, agentId: null,                   isRag: false, supportsManualOverride: false, supportsCostEstimate: false, allowedModels: undefined as string[] | undefined, obs: NONE_OBS },
  { initials: 'SA', cls: 'av-pu', name: 'Strategy Agent',         model: 'Claude Haiku',           status: 'ok', active: false, agentId: null,                   isRag: false, supportsManualOverride: false, supportsCostEstimate: false, allowedModels: undefined as string[] | undefined, obs: NONE_OBS },
];

const TrashIcon = ({ size = 10 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

interface IAgentsSidebarProps {
  selectedAgent: number;
  setSelectedAgent: (i: number) => void;
  sessions: ILsRagSession[];
  activeSession: string | null;
  setActiveSession: (id: string) => void;
  deletingSess: string | null;
  handleDeleteSession: (id: string) => void;
  documents: ILsRagDocument[];
  uploading: boolean;
  handleUpload: (file: File) => void;
  deletingDoc: string | null;
  handleDelete: (id: string) => void;
  handleNewSession: () => void;
}

const AgentsSidebar: React.FC<IAgentsSidebarProps> = ({
  selectedAgent, setSelectedAgent,
  sessions, activeSession, setActiveSession, deletingSess, handleDeleteSession,
  documents, uploading, handleUpload, deletingDoc, handleDelete,
  handleNewSession,
}) => (
  <div className="cop-l">
    <div className="cop-l-hdr">
      <div className="cop-lbl">Configured Agents</div>
    </div>

    <div className="cop-ls-body">
      {AGENTS.map((ag, i) => (
        <div
          key={ag.name}
          className={`cop-ag${selectedAgent === i ? ' sel' : ''}${!ag.active ? ' cop-ag-inactive' : ''}`}
          onClick={() => { if (ag.active) setSelectedAgent(i); }}
          title={!ag.active ? 'Coming soon' : undefined}
        >
          <div className={`av ${ag.cls}`}>{ag.initials}</div>
          <div className="cop-ag-inf">
            <div className="cop-ag-n">{ag.name}</div>
            <div className="cop-ag-m">{ag.active ? 'Online' : 'Coming soon'}</div>
          </div>
          {ag.active ? <div className={`cop-od od-${ag.status}`} /> : <div className="cop-od od-cs" />}
        </div>
      ))}
    </div>

    {sessions.length > 0 && (
      <div className="cop-ses-sec">
        <div className="cop-ses-lbl">Sessions</div>
        {sessions.map(sess => (
          <div
            key={sess.id}
            className={`cop-sess${activeSession === sess.id ? ' sel' : ''}`}
            onClick={() => setActiveSession(sess.id)}
          >
            <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor" style={{ flexShrink: 0, color: '#9ca3af' }}>
              <path d="M8 1C4.1 1 1 4.1 1 8s3.1 7 7 7 7-3.1 7-7-3.1-7-7-7zm0 12.5C4.9 13.5 2.5 11.1 2.5 8S4.9 2.5 8 2.5 13.5 4.9 13.5 8 11.1 13.5 8 13.5zM8.5 4h-1v5l3.5 2 .5-.9-3-1.8V4z"/>
            </svg>
            <span className="cop-sess-n">{sess.name || 'Session'}</span>
            <button
              className="cop-sess-del"
              title="Remove session"
              disabled={deletingSess === sess.id}
              onClick={e => { e.stopPropagation(); handleDeleteSession(sess.id); }}
            >
              <TrashIcon />
            </button>
          </div>
        ))}
      </div>
    )}

    {/* Knowledge base / PDF indexing is a ls-rag-only concept (pgvector
        retrieval) — hidden entirely for the other agents rather than shown
        empty, since "upload a document" isn't an action those agents support. */}
    {AGENTS[selectedAgent]?.isRag && (
    <div className="cop-doc-sec">
      <div className="cop-doc-lbl">Knowledge Documents</div>
      <Upload accept=".pdf" showUploadList={false} beforeUpload={(file) => { handleUpload(file); return false; }}>
        <button className="cop-up" disabled={uploading}>
          {uploading ? <Spin size="small" /> : '📎'}
          {uploading ? ' Indexing…' : ' Upload Documents'}
        </button>
      </Upload>
      <div className="cop-doc-list">
        {documents.length === 0 ? (
          <div className="cop-no-docs">No documents indexed yet</div>
        ) : (
          documents.map(doc => (
            <div key={doc.id} className="cop-di">
              <svg className="cop-di-icon" width="12" height="12" viewBox="0 0 16 16" fill="#9ca3af">
                <rect x="3" y="1" width="8" height="14" rx="1"/><rect x="5" y="5" width="6" height="1.5" rx=".5"/><rect x="5" y="8" width="6" height="1.5" rx=".5"/>
              </svg>
              <span className="cop-dn">{doc.drug_name || doc.name}</span>
              <span className="cop-dt">indexed</span>
              <button
                className="cop-di-del"
                title="Remove document"
                disabled={deletingDoc === doc.id}
                onClick={() => handleDelete(doc.id)}
              >
                <TrashIcon size={11} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
    )}

    {/* Same class/style/placement as Copilot Queries' "Start New Session"
        button, for a consistent look across all 4 tabs. */}
    <Button className="new-session-button" onClick={handleNewSession}>
      <Typography className="select-button-text">
        <PlusCircleFilled /> {'New Agent Session'}
      </Typography>
    </Button>
  </div>
);

export default AgentsSidebar;
